import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { VERSION } from '../app.constants';
import { JhiLanguageService } from '../shared/core-utils';
import { LoginService } from '../shared/login/login.service';
import { LoginPageDetails } from '../shared/login/login.model';
import { UserProfileModel } from '../entities/user-profile/model/user-profile.model';
import { LoginContext } from '../shared/login/login-context';
import { AlertService } from '../shared/alert/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ModalAlertComponent } from '../shared/modal/modal-alert.component';
import { Subscription } from 'rxjs';
import { PckeAuthorizationService } from '../shared/auth/pcke-authorization.service';

@Component({
    selector: 'jhi-login',
    templateUrl: './login.component.html',
    styleUrls: [
        './login.component.scss'
    ],
    encapsulation: ViewEncapsulation.None
})
export class LoginComponent implements OnInit, OnDestroy {

    version = VERSION;
    pageDetails: LoginPageDetails;
    model: UserProfileModel = { username: '', password: '', rememberMe: 'on' };

    display_name: string;
    return_url: string;
    client_id: string;
    redirect_uri: string;
    email_sent: string;
    code_challenge: string;
    code_challenge_method: string;
    state: string;

    isLoginFormValid = true;
    isLoading = false;
    errorMessage: string;
    errorFields = [];

    private queryParamSubscription: Subscription;

    constructor(private context: LoginContext,
                private route: ActivatedRoute,
                private alertService: AlertService,
                private languageService: JhiLanguageService,
                private loginService: LoginService,
                private translateService: TranslateService,
                public modalService: NgbModal,
                private router: Router,
                private pckeAuthorizationService: PckeAuthorizationService
    ) {
        document.body.classList.add('body-container');
        this.queryParamSubscription = this.route.queryParams.subscribe((params) => {
            this.display_name = params['display_name'];
            this.return_url = params['return_url'];
            this.client_id = params['client_id'];
            this.redirect_uri = params['redirect_uri'];
            this.email_sent = params['email_sent'];
            this.code_challenge = params['code_challenge'];
            this.code_challenge_method = params['code_challenge_method'];
            this.state = params['state'];
        });

    }

    ngOnInit(): void {

        // Silently check whether the browser already holds a valid session token,
        // without triggering a forced logout if validation fails (see skipAuthExpiredIntercept flag).
        this.loginService.validateToken(true).toPromise().then(() => {

            // Session is valid. If this page load originated from an OAuth
            // request (all required OAuth params present), skip the login form entirely
            // and immediately submit the authorization request to issue a fresh code —
            // this is the silent re-authentication path for the OAuth/PKCE flow.
            if (this.client_id && this.redirect_uri && this.code_challenge && this.code_challenge_method) {
                this.pckeAuthorizationService.doAuthorizeSubmit({
                    clientId: this.client_id,
                    redirectUri: this.redirect_uri,
                    codeChallenge: this.code_challenge,
                    codeChallengeMethod: this.code_challenge_method,
                    state: this.state
                });
            } else if (this.display_name && this.return_url) {
                // Redirect to the return_url if specified
                this.redirectToReturnURL();
            } else {
                // Not an OAuth/PKCE request — this is a standard direct login to BMS.
                // Skip rendering the login page and redirect straight to the main app,
                // avoiding an unnecessary re-authentication prompt.
                this.redirectToMainApp();
            }
        });

        this.loginService.getLoginPageDetails().toPromise().then((response) => {
            this.pageDetails = response;
            this.context.pageDetails = this.pageDetails;
        });
    }

    ngOnDestroy(): void {
        document.body.classList.remove('body-container');
        this.queryParamSubscription.unsubscribe();
    }

    toggleRememberMe() {
        const isChecked = this.model.rememberMe === 'on';
        this.model.rememberMe = isChecked ? 'off' : 'on';
    }

    submitLoginForm(): void {
        this.clearErrors();
        const validateLicense = this.pageDetails.isLicenseValidationEnabled;
        this.isLoading = true;
        if (validateLicense) {
            const CONTACT_SUPPORT_MSG = ' Please contact <a '
                + 'href="https://ibplatform.atlassian.net/servicedesk/customer/portal/4/group/25/create/51" target="_blank">'
                + 'IBP support</a>.';

            this.loginService.getBVDesignLicense().toPromise().then((data) => {
                if (data && data.length > 0) {
                    const expiry = data[0].expiry;
                    const expiryDays = data[0].expiryDays;

                    if (expiryDays) {
                        // If BV Licence is expiring, just show a warning, a user should be able to proceeed to logging in.
                        if (expiryDays <= 30 && expiryDays > 0) {
                            this.showWarningModal('Your organization\'s BMS license is going to expire soon (' + expiry + ').'
                                + CONTACT_SUPPORT_MSG);
                        }
                    }
                }

                this.validateLogin();
            }).catch((response) => {
                // Continue to login if there is no license.
                this.validateLogin();
            });
        } else {
            this.validateLogin();
        }
    }

    validateLogin(): void {
        // Validate login credential through BMSAPI. This will return the token
        // that will be stored later as Http Secure Cookie. This token will be used for BMSAPI endpoints authentication
        this.loginService.validateLogin(this.model).toPromise().then((response) => {
            if (response && response.requireOneTimePassword) {
                this.doCreateOTP();
            } else {
                this.processLoginSuccess();
            }
        }).catch((response) => {
            this.applyValidationErrors(response ? response.error.errors : {});
        });
    }

    goToForgotPassword(): void {
        this.context.model = this.model;
        this.router.navigate(['/forgot-password'], {
            queryParams: {}
        });
    }

    async processLoginSuccess() {

        // Manually renew session in Fieldbook and GDMS after successful authentication to avoid session fixation
        try {
            await this.loginService.renewFieldbookSession().toPromise();
        } catch (error) {
            this.displayClientError('System failed to renew session. Please contact your system administrator.');
            return;
        }

        // Aside from generating token from validateLogin,
        // We need to initialize Spring Security authentication for Workbench
        this.loginService.authorizeWorkbench(this.model).toPromise().then((request) => {
            this.isLoading = false;
            // This service will always return successful since we already
            // authenticated the user via validateLogin
            if (this.display_name && this.return_url) {
                this.redirectToReturnURL();
            } else if (this.client_id && this.redirect_uri) {
                this.redirectToAuthorizeLogin();
            } else {
                this.redirectToMainApp();
            }
        });

    }

    private redirectToMainApp() {
        this.context.recentlyLogin = true;
        this.router.navigate(['']);
    }

    private redirectToReturnURL() {
        const validatedReturnUrl = this.getValidatedReturnUrl(this.return_url);
        if (validatedReturnUrl) {
            window.top.location.href = validatedReturnUrl;
        } else {
            this.displayClientError('Invalid return URL.');
        }
    }

    private redirectToAuthorizeLogin() {
        this.router.navigate(['/authorize-login'], {
            queryParams: {
                display_name: this.display_name,
                return_url: this.return_url,
                client_id: this.client_id,
                redirect_uri: this.redirect_uri,
                code_challenge: this.code_challenge,
                code_challenge_method: this.code_challenge_method,
                state: this.state
            }
        });
    }


    private getValidatedReturnUrl(returnUrl: string): string | null {
        if (!returnUrl) {
            return null;
        }

        try {
            const validatedUrl = new URL(returnUrl, window.location.origin);
            return validatedUrl.origin === window.location.origin ? validatedUrl.href : null;
        } catch (error) {
            return null;
        }
    }

    private displayClientError(errorMsg) {
        this.errorMessage = errorMsg;
        this.isLoginFormValid = false;
        this.isLoading = false;
    }

    private applyValidationErrors(errors) {
        let errorMessage = '';

        Object.keys(errors).forEach((fieldName) => {
            this.errorFields.push(fieldName);
            errorMessage += errorMessage ? (' ' + errors[fieldName]) : errors[fieldName];
        });
        this.displayClientError(errorMessage);
    }

    private clearErrors() {
        // Double check if form is actually invalid, as this function may occasionally be called unnecessarily
        if (!this.isLoginFormValid) {
            // Clear client errors
            this.errorMessage = '';
            this.isLoginFormValid = true;
            this.errorFields = [];
        }
    }

    redirectToOneTimePasswordScreen() {
        this.context.model = this.model;
        this.router.navigate(['/one-time-password'], { queryParams: {
            display_name: this.display_name,
            return_url: this.return_url,
            client_id: this.client_id,
            redirect_uri: this.redirect_uri,
            code_challenge: this.code_challenge,
            code_challenge_method: this.code_challenge_method,
            state: this.state
        } });
    }

    doCreateOTP() {
        const request = {
            username: this.model.username,
            password: this.model.password
        };

        this.loginService.createOTP(request).toPromise().then((response) => {
            this.isLoading = false;
            this.redirectToOneTimePasswordScreen();
        }).catch((response) => {
            this.applyValidationErrors(response ? response.error.errors : {});
        });
    }

    private async showWarningModal(message) {
        const confirmModalRef = this.modalService.open(ModalAlertComponent as Component,
            { modalDialogClass: 'modal-md', backdrop: 'static' });
        confirmModalRef.componentInstance.message = message;
        confirmModalRef.componentInstance.showCancelButton = false;
        try {
            await confirmModalRef.result;
        } catch (rejected) {
            return false;
        }
        return true;
    }
}
